/**
 * Pictogramele vitrinei: SVG-uri inline, fara nicio dependinta.
 *
 * Un set mic, desenat o singura data, cu aceeasi grosime de linie si acelasi
 * capat rotunjit, ca butoanele si etichetele sa arate ca o singura familie.
 * Toate iau culoarea din `currentColor`, deci merg pe orice fundal al temei si
 * pe tema inchisa fara tratament separat. Sunt DECOR: `aria-hidden` implicit —
 * numele accesibil il da textul sau `aria-label`-ul butonului care le contine.
 */

import type { SVGProps } from "react";

interface IconProps extends Omit<SVGProps<SVGSVGElement>, "children"> {
  size?: number;
}

function Icon({ size = 20, children, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconPin = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 21s-6.5-5.7-6.5-11a6.5 6.5 0 0 1 13 0c0 5.3-6.5 11-6.5 11Z" />
    <circle cx="12" cy="10" r="2.4" />
  </Icon>
);
export const IconLocate = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="3.2" />
    <circle cx="12" cy="12" r="8" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
  </Icon>
);
export const IconSearch = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.2-4.2" />
  </Icon>
);
export const IconClock = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 1.8" />
  </Icon>
);
export const IconBike = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="5.5" cy="17" r="3" />
    <circle cx="18.5" cy="17" r="3" />
    <path d="M5.5 17 9 9h4l3 5.5M13 9l2.5 8M9 9H6.5M15 6.5h2.2l1.3 3.5" />
  </Icon>
);
export const IconCash = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
    <circle cx="12" cy="12" r="2.6" />
    <path d="M6 9.5h.01M18 14.5h.01" />
  </Icon>
);
export const IconCard = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
    <path d="M2.5 10h19M6.5 14.5h4" />
  </Icon>
);
export const IconChevronRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="m9 6 6 6-6 6" />
  </Icon>
);
export const IconChevronDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6 9 6 6 6-6" />
  </Icon>
);
export const IconArrowLeft = (p: IconProps) => (
  <Icon {...p}>
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </Icon>
);
export const IconCheck = (p: IconProps) => (
  <Icon {...p}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </Icon>
);
export const IconClose = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Icon>
);
export const IconPlus = (p: IconProps) => (
  <Icon {...p} strokeWidth={2.4}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);
export const IconMinus = (p: IconProps) => (
  <Icon {...p} strokeWidth={2.4}>
    <path d="M5 12h14" />
  </Icon>
);
export const IconHeart = ({ filled = false, ...p }: IconProps & { filled?: boolean }) => (
  <Icon {...p} fill={filled ? "currentColor" : "none"}>
    <path d="M20.8 4.7a5.5 5.5 0 0 0-7.8 0L12 5.8l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.4 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" />
  </Icon>
);
export const IconPhone = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 4h3.5l1.8 4.3-2.2 1.6a12 12 0 0 0 6 6l1.6-2.2L20 15.5V19a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2Z" />
  </Icon>
);
export const IconInfo = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5M12 7.8h.01" />
  </Icon>
);
export const IconCart = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 4h2.2l2.3 11.2a1.5 1.5 0 0 0 1.5 1.2h8.6a1.5 1.5 0 0 0 1.5-1.2L21 8H6.5" />
    <circle cx="9.5" cy="20" r="1.3" />
    <circle cx="17" cy="20" r="1.3" />
  </Icon>
);
export const IconStar = (p: IconProps) => (
  <Icon {...p} fill="currentColor" stroke="none">
    <path d="m12 3.2 2.7 5.6 6.1.8-4.5 4.2 1.2 6L12 16.9l-5.5 2.9 1.2-6L3.2 9.6l6.1-.8L12 3.2Z" />
  </Icon>
);
export const IconTag = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 12.5V4.5a1 1 0 0 1 1-1h8l8 8-8 8-9-7Z" />
    <path d="M8 8h.01" />
  </Icon>
);
export const IconHome = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 11 12 4l8.5 7M6 9.8V20h12V9.8" />
  </Icon>
);
export const IconReceipt = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 3.5h12v17l-2.5-1.5-2.5 1.5-1-.6-1 .6-2.5-1.5L6 20.5z" />
    <path d="M9 8h6M9 11.5h6M9 15h4" />
  </Icon>
);
export const IconMenu = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Icon>
);
export const IconEdit = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3Z" />
    <path d="m13.5 6.5 3 3" />
  </Icon>
);
export const IconTrash = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 7h16M9.5 7V4.5h5V7M6.5 7l.8 12.5h9.4L17.5 7M10 11v5M14 11v5" />
  </Icon>
);
export const IconStore = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 9.5 5.5 4h13L20 9.5M4 9.5a3 3 0 0 0 5.3 1.9A3 3 0 0 0 12 13a3 3 0 0 0 2.7-1.6A3 3 0 0 0 20 9.5M5.5 12v8h13v-8M10 20v-5h4v5" />
  </Icon>
);
export const IconTimer = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="13" r="7.5" />
    <path d="M12 9.5V13l2.5 1.5M9.5 3h5M12 3v2.5M18.5 6.5l1.2-1.2" />
  </Icon>
);
export const IconChef = (p: IconProps) => (
  <Icon {...p}>
    <path d="M7 14.5V19h10v-4.5" />
    <path d="M6.5 14.5A3.5 3.5 0 0 1 7.2 7.6 4.8 4.8 0 0 1 16.8 7.6a3.5 3.5 0 0 1 .7 6.9Z" />
  </Icon>
);
export const IconPackage = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3 20 7.5v9L12 21l-8-4.5v-9L12 3Z" />
    <path d="M4 7.5 12 12l8-4.5M12 12v9" />
  </Icon>
);
export const IconSparkle = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6" />
  </Icon>
);
export const IconLeaf = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 19c0-8 5-13 14-14-.5 9-5.5 14-13 14" />
    <path d="M5 19c3-3 6-6 9-9" />
  </Icon>
);
export const IconWarning = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4 2.8 19.5h18.4L12 4Z" />
    <path d="M12 10v4M12 17h.01" />
  </Icon>
);
export const IconTruck = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 6.5h11v9H3zM14 10h4l3 3v2.5h-7z" />
    <circle cx="7" cy="17.5" r="1.8" />
    <circle cx="17.5" cy="17.5" r="1.8" />
  </Icon>
);
export const IconRepeat = (p: IconProps) => (
  <Icon {...p}>
    <path d="M17 2.5 21 6.5l-4 4" />
    <path d="M3 11.5v-2a3 3 0 0 1 3-3h15" />
    <path d="m7 21.5-4-4 4-4" />
    <path d="M21 12.5v2a3 3 0 0 1-3 3H3" />
  </Icon>
);
